import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Lock,
  FileType,
  Check,
  Ban,
  ReceiptText,
  Pencil,
  Send,
  FileCheck2,
  Paperclip,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileText,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { useAuth } from "../../hooks/useAuth";
import {
  getInvoice,
  setInvoiceStatus,
  cancelInvoice,
  createCreditNote,
} from "../../store/slices/invoicesSlice";
import { invoiceAPI } from "../../services/api";
import Button from "../../components/UI/Button";
import { Input } from "../../components/UI/FormField";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import {
  money,
  fmtDate,
  STATUS_BADGE,
  containerAuditRefs,
} from "./invoiceUtils";
import InvoicePreview from "../../components/invoices/InvoicePreview";
import type { InvoiceStatus, VatCategory } from "../../types/invoice";

const InvoiceDetail: React.FC = () => {
  const { t } = useTranslation("invoicing");
  const { id = "" } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const { current, loading, saving } = useAppSelector((s) => s.invoices);

  const [creditOpen, setCreditOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [credit, setCredit] = useState<
    Record<string, { on: boolean; amount: number }>
  >({});

  const vatPct = (c: VatCategory) =>
    c === "24" ? t("vat.24") : c === "0" ? t("vat.0") : t("vat.noTax");

  useEffect(() => {
    if (id) dispatch(getInvoice(id));
  }, [dispatch, id]);

  if (loading && (!current || current._id !== id)) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!current || current._id !== id) {
    return (
      <div className="p-6 text-sm text-[#8A9590]">{t("detail.notFound")}</div>
    );
  }

  const inv = current;
  const isCredit = inv.invoiceType === "credit_note";
  const isDraft = inv.status === "draft";
  const isLocked = ["sent", "overdue", "paid"].includes(inv.status);

  const statusText = isCredit ? t("status.creditNote") : t(`status.${inv.status}`);

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const res = await invoiceAPI.getPdfBlob(id);
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(t("detail.pdfFailed"));
    } finally {
      setPdfLoading(false);
    }
  };

  const markPaid = async () => {
    const res = await dispatch(setInvoiceStatus({ id, status: "paid" }));
    if (setInvoiceStatus.fulfilled.match(res)) toast.success(t("detail.markedPaid"));
    else toast.error((res.payload as string) || t("detail.actionFailed"));
  };

  const doCancel = async () => {
    const res = await dispatch(cancelInvoice({ id }));
    if (cancelInvoice.fulfilled.match(res)) toast.success(t("detail.cancelledToast"));
    else toast.error((res.payload as string) || t("detail.actionFailed"));
  };

  const openCredit = () => {
    const initial: Record<string, { on: boolean; amount: number }> = {};
    for (const l of inv.lines) {
      if (l._id) initial[l._id] = { on: false, amount: Math.abs(l.lineTotal || 0) };
    }
    setCredit(initial);
    setCreditOpen(true);
  };

  const submitCredit = async () => {
    const selected = inv.lines
      .filter((l) => l._id && credit[l._id]?.on)
      .map((l) => ({
        originalLineId: l._id!,
        quantity: l.quantity,
        creditAmount: credit[l._id!].amount,
      }));
    if (selected.length === 0) {
      toast.error(t("detail.selectLineToCredit"));
      return;
    }
    const res = await dispatch(
      createCreditNote({ id, data: { lines: selected, sendEmail: true } }),
    );
    if (createCreditNote.fulfilled.match(res)) {
      toast.success(t("detail.creditIssued"));
      setCreditOpen(false);
      navigate(`/invoices/${res.payload._id}`);
    } else {
      toast.error((res.payload as string) || t("detail.creditFailed"));
    }
  };

  const containerRefs = containerAuditRefs(inv.lines);

  // What the frozen specification actually describes, so the indicator does not
  // announce a "shipment specification" on a containers-only invoice. Read off
  // the blocks themselves when they exist (a locked invoice has them); a draft
  // has none yet, so fall back to what its lines are billed from.
  const specHasShipments = inv.specification?.length
    ? inv.specification.some((b) => b.sourceType !== "containerLine")
    : inv.lines.some((l) => l.shipmentId);
  const specHasContainers = inv.specification?.length
    ? inv.specification.some((b) => b.sourceType === "containerLine")
    : inv.lines.some((l) => l.containerLineId);
  const specIncludedKey =
    specHasShipments && specHasContainers
      ? "detail.specIncludedBoth"
      : specHasContainers
        ? "detail.specIncludedContainers"
        : "detail.specIncluded";
  // Only claim a specification that exists. Once locked the blocks are frozen,
  // so they are the truth; a draft has none yet, so it is judged on whether its
  // lines could produce one at all.
  const specAppended =
    !!inv.includeSpecification &&
    (isDraft
      ? specHasShipments || specHasContainers
      : (inv.specification?.length ?? 0) > 0);

  const creditTotal = inv.lines.reduce(
    (sum, l) =>
      l._id && credit[l._id]?.on ? sum + (credit[l._id].amount || 0) : sum,
    0,
  );

  return (
    <div className="flex w-full h-full min-h-0">
      <div className="flex-1 min-w-0 h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-lg font-medium text-[#0B0F0E] truncate">
                {inv.invoiceNumber}
              </h1>
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${
                  STATUS_BADGE[inv.status as InvoiceStatus]
                }`}
              >
                {statusText}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {isDraft && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/invoices/${id}/edit`)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> {t("detail.edit")}
                </Button>
              )}
              {(isLocked || isCredit) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePdf}
                  disabled={pdfLoading}
                >
                  <FileType className="h-3.5 w-3.5 mr-1" />{" "}
                  {pdfLoading ? "…" : t("detail.pdf")}
                </Button>
              )}
              {isLocked && !isCredit && (
                <Button variant="outline" size="sm" onClick={openCredit}>
                  <ReceiptText className="h-3.5 w-3.5 mr-1" /> {t("detail.creditNote")}
                </Button>
              )}
              {isLocked && !isCredit && isAdmin && inv.status !== "paid" && (
                <Button variant="primary" size="sm" onClick={markPaid} disabled={saving}>
                  <Check className="h-3.5 w-3.5 mr-1" /> {t("detail.markPaid")}
                </Button>
              )}
              {isLocked && !isCredit && isAdmin && inv.status !== "paid" && (
                <Button variant="danger" size="sm" onClick={doCancel} disabled={saving}>
                  <Ban className="h-3.5 w-3.5 mr-1" /> {t("detail.cancel")}
                </Button>
              )}
            </div>
          </div>

          {inv.status === "sent" && (
            <div className="flex items-center gap-2 rounded-lg bg-[#E6F1FB] border border-[#B5D4F4] text-[#0C447C] px-4 py-2.5 text-[13px]">
              <Send className="h-4 w-4 shrink-0" />
              {t("detail.sentBanner", {
                doc: isCredit ? t("detail.creditNoteWord") : t("detail.invoiceWord"),
                email: inv.sentToEmail || "—",
                date: fmtDate(inv.sentAt),
              })}
            </div>
          )}

          {/* Parties */}
          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="text-[11px] text-[#8A9590] mb-1">
                {t("detail.billedTo")}
              </div>
              <div className="font-medium">{inv.clientSnapshot?.companyName}</div>
              <div className="text-[#6b6b67]">
                {inv.clientSnapshot?.registerationCode}
              </div>
              <div className="text-[#6b6b67]">{inv.clientSnapshot?.email}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[11px] text-[#8A9590]">
                  {t("detail.invoiceDate")}
                </div>
                <div>{fmtDate(inv.invoiceDate)}</div>
              </div>
              {!isCredit && (
                <div>
                  <div className="text-[11px] text-[#8A9590]">
                    {t("detail.dueDate")}
                  </div>
                  <div>{fmtDate(inv.dueDate)}</div>
                </div>
              )}
              <div>
                <div className="text-[11px] text-[#8A9590]">
                  {t("detail.currency")}
                </div>
                <div>{inv.currency}</div>
              </div>
              {inv.poReference && (
                <div>
                  <div className="text-[11px] text-[#8A9590]">
                    {t("detail.poRef")}
                  </div>
                  <div>{inv.poReference}</div>
                </div>
              )}
            </div>
          </div>

          {/* Lines */}
          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)]">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[520px]">
                <thead className="bg-[#F5F4F0] text-[#8A9590]">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">
                      {t("detail.colDescription")}
                    </th>
                    <th className="text-right font-medium px-3 py-2">
                      {t("detail.colVat")}
                    </th>
                    <th className="text-right font-medium px-3 py-2">
                      {t("detail.colQty")}
                    </th>
                    <th className="text-right font-medium px-3 py-2">
                      {t("detail.colUnitPrice")}
                    </th>
                    <th className="text-right font-medium px-3 py-2">
                      {t("detail.colTotal")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {inv.lines.map((l, i) => (
                    <tr key={i} className="border-t border-[rgba(11,15,14,0.06)]">
                      <td className="px-3 py-2">
                        {l.description}
                        {(l.shipmentNumber ||
                          l.workNumber ||
                          l.containerLineNumber ||
                          l.sourceServiceRef) && (
                          <span className="block text-[10px] text-[#9b9b96]">
                            {[
                              l.shipmentNumber ||
                                l.workNumber ||
                                l.containerLineNumber,
                              l.containerNumber,
                              l.sourceServiceRef,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-[#6b6b67]">
                        {vatPct(l.vatCategory)}
                      </td>
                      <td className="px-3 py-2 text-right">{l.quantity}</td>
                      <td className="px-3 py-2 text-right">{money(l.unitPrice)}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {money(l.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t border-[rgba(11,15,14,0.06)] p-4 flex justify-end">
              <div className="w-full max-w-xs space-y-1 text-[13px]">
                {inv.vatGroups.map((g) => (
                  <React.Fragment key={g.vatCategory}>
                    <div className="flex justify-between text-[#6b6b67]">
                      <span>
                        {g.vatCategory === "NO_TAX"
                          ? t("vat.subtotalNoVat")
                          : g.vatCategory === "24"
                            ? t("vat.subtotal24")
                            : t("vat.subtotal0")}
                      </span>
                      <span>{money(g.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[#6b6b67]">
                      <span>
                        {g.vatCategory === "NO_TAX"
                          ? t("vat.outsideScope")
                          : g.vatCategory === "24"
                            ? t("vat.vat24")
                            : t("vat.vat0")}
                      </span>
                      <span>{money(g.vatAmount)}</span>
                    </div>
                  </React.Fragment>
                ))}
                <div
                  className={`flex justify-between font-medium text-[15px] border-t border-[rgba(11,15,14,0.1)] pt-2 mt-1 ${
                    isCredit ? "text-[#A32D2D]" : ""
                  }`}
                >
                  <span>
                    {isCredit ? t("detail.totalCreditEur") : t("detail.totalEur")}
                  </span>
                  <span>{money(inv.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Container references — the audit trail for container-billed lines:
              which physical box, on which booking, under whose reference. One row
              per container, matching the block on the generated PDF. */}
          {containerRefs.length > 0 && (
            <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4">
              <div className="text-[11px] text-[#8A9590] mb-2 uppercase tracking-wide">
                {t("detail.containerReferences")}
              </div>
              <div className="space-y-1.5">
                {containerRefs.map((r) => (
                  <div
                    key={r.key}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px]"
                  >
                    <span className="font-medium text-[#0B0F0E]">
                      {r.lineNumber}
                    </span>
                    <span className="text-[#6b6b67]">
                      {[
                        r.containerNumber
                          ? t("preview.refContainer", { value: r.containerNumber })
                          : "",
                        r.orderNumber
                          ? t("preview.refOrder", { value: r.orderNumber })
                          : "",
                        r.orderReference
                          ? t("preview.refClientRef", { value: r.orderReference })
                          : "",
                        r.bookingRef
                          ? t("preview.refBooking", { value: r.bookingRef })
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specification indicator + attached signed documents */}
          {(specAppended || (inv.attachedSignedDocuments?.length ?? 0) > 0) && (
            <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4 space-y-3">
              {specAppended && (
                <div className="flex items-center gap-2 text-[13px] text-[#0B0F0E]">
                  <FileCheck2 className="h-4 w-4 text-[#0C447C] shrink-0" />
                  {t(specIncludedKey)}
                </div>
              )}
              {(inv.attachedSignedDocuments?.length ?? 0) > 0 && (
                <div>
                  <div className="text-[11px] text-[#8A9590] mb-1.5 uppercase tracking-wide">
                    {t("detail.attachedDocuments")}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {inv.attachedSignedDocuments!.map((d) => (
                      <div
                        key={d.documentId}
                        className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-md border border-gray-200"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                          <span className="text-[12px] text-gray-900 truncate">
                            {d.originalName}
                          </span>
                        </div>
                        <button
                          onClick={() => window.open(d.fileUrl, "_blank")}
                          className="p-1 text-gray-500 hover:text-blue-600 transition-colors shrink-0"
                          title={t("detail.view")}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Credit note panel */}
          {creditOpen && (
            <div className="bg-white rounded-md border border-[#FAC775] shadow-sm">
              <div className="px-4 py-3 border-b border-[rgba(11,15,14,0.06)] bg-[#FAEEDA] text-[#633806] text-[13px] font-medium">
                {t("detail.creditPanelTitle", { number: inv.invoiceNumber })}
              </div>
              <div className="divide-y divide-[rgba(11,15,14,0.06)]">
                {inv.lines.map((l) => {
                  if (!l._id) return null;
                  const c = credit[l._id];
                  return (
                    <div key={l._id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                      <input
                        type="checkbox"
                        checked={!!c?.on}
                        onChange={(e) =>
                          setCredit((prev) => ({
                            ...prev,
                            [l._id!]: { ...prev[l._id!], on: e.target.checked },
                          }))
                        }
                        className="h-4 w-4 accent-[#C97A3C]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{l.description}</div>
                        <div className="text-[11px] text-[#8A9590]">
                          {t("detail.original", {
                            amount: money(l.lineTotal),
                            vat: vatPct(l.vatCategory),
                          })}
                        </div>
                      </div>
                      <div className="w-32">
                        <Input
                          size="sm"
                          type="number"
                          step="0.01"
                          className="text-right"
                          disabled={!c?.on}
                          value={c?.amount ?? 0}
                          onChange={(e) =>
                            setCredit((prev) => ({
                              ...prev,
                              [l._id!]: {
                                ...prev[l._id!],
                                amount: Number(e.target.value),
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-3 border-t border-[rgba(11,15,14,0.06)] flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-[13px] text-[#6b6b67]">
                  {t("detail.totalCredit")}{" "}
                  <strong className="text-[#A32D2D]">-{money(creditTotal)}</strong>
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setCreditOpen(false)}>
                    {t("detail.cancel")}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={submitCredit}
                    disabled={saving}
                  >
                    <ReceiptText className="h-3.5 w-3.5 mr-1" />{" "}
                    {t("detail.issueCreditNote")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {inv.timeline?.length > 0 && (
            <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4">
              <div className="text-[11px] text-[#8A9590] mb-2 uppercase tracking-wide">
                {t("detail.history")}
              </div>
              <ul className="space-y-1 text-[12px] text-[#6b6b67]">
                {inv.timeline.map((ev, i) => (
                  <li key={i} className="flex items-center gap-2 flex-wrap">
                    <Lock className="h-3 w-3 text-[#9b9b96] shrink-0" />
                    <span className="capitalize font-medium text-[#0B0F0E]">
                      {ev.action}
                    </span>
                    {ev.note && <span>· {ev.note}</span>}
                    {ev.byName && <span>· {ev.byName}</span>}
                    <span className="text-[#9b9b96]">· {fmtDate(ev.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* The document itself, beside the record of it — the same collapsible
          right-hand panel the editor uses, so a locked invoice reads the way it
          was composed. The exact PDF is still one click away on "PDF". */}
      <aside
        className={`hidden lg:flex flex-col h-full shrink-0 relative border-l border-[rgba(11,15,14,0.08)] bg-[#F1EFE8] ${
          previewCollapsed ? "lg:w-12" : "lg:w-[46%]"
        } transition-[width] duration-300`}
      >
        <button
          type="button"
          onClick={() => setPreviewCollapsed((c) => !c)}
          className="absolute top-3 left-1.5 z-10 bg-white border border-[rgba(11,15,14,0.12)] rounded-full p-1.5 shadow-sm hover:bg-[#FAFAF8] transition-colors"
          title={t(
            previewCollapsed ? "detail.expandPreview" : "detail.collapsePreview",
          )}
          aria-label={t(
            previewCollapsed ? "detail.expandPreview" : "detail.collapsePreview",
          )}
        >
          {previewCollapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {previewCollapsed ? (
          // A full-height rail rather than a floating card: collapsed, the
          // panel is a strip you click, and its label reads down it.
          <div className="flex-1 flex flex-col items-center gap-3 pt-14 text-[#8A9590]">
            <FileText className="h-5 w-5" />
            <span className="[writing-mode:vertical-rl] text-[11px] font-medium tracking-wide">
              {t("detail.previewTitle")}
            </span>
          </div>
        ) : (
          // The sheet scrolls inside the panel, so the form beside it keeps its
          // own scroll position instead of the two fighting over the page's.
          <div className="flex-1 min-h-0 overflow-y-auto px-5">
            {/* The sheet fills the panel exactly and no more: `my-4` plus a
                min-height of the panel less those same margins adds up to 100%,
                so a short invoice still looks like a full page but produces no
                scrollbar. A long one overflows and scrolls — inside this panel
                only, which is why the form column beside it has its own. */}
            <div className="mx-auto my-4 w-full max-w-[720px] min-h-[calc(100%-2rem)] bg-white border border-[rgba(11,15,14,0.12)] shadow-sm rounded-sm p-7">
              <InvoicePreview
                invoiceNumber={
                  inv.status === "draft" ? undefined : inv.invoiceNumber
                }
                invoiceType={inv.invoiceType}
                org={inv.orgSnapshot}
                client={inv.clientSnapshot}
                invoiceDate={inv.invoiceDate}
                dueDate={inv.dueDate}
                paymentTerms={inv.paymentTerms}
                poReference={inv.poReference}
                costCentre={inv.costCentre}
                projectCode={inv.projectCode}
                lines={inv.lines}
                vatGroups={inv.vatGroups}
                total={inv.total}
                includeSpecification={inv.includeSpecification}
                specificationCount={inv.specification?.length ?? 0}
              />
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

export default InvoiceDetail;
